import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getBidImageUrl, normalizeBidImageUrl, resetImageRetryState, retryImageLoad } from "../asset/js/retryImageLoad";

export default function BidImageLightbox({ images = [], initialImage = "", open, onClose }) {
  const cleanImages = useMemo(
    () => [...new Set(images.map(normalizeBidImageUrl).filter(Boolean))],
    [images]
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!open || cleanImages.length === 0) return;
    const requestedIndex = cleanImages.indexOf(normalizeBidImageUrl(initialImage));
    setCurrentIndex(requestedIndex >= 0 ? requestedIndex : 0);
  }, [cleanImages, initialImage, open]);

  const showPrevious = () => {
    setCurrentIndex((index) => (index - 1 + cleanImages.length) % cleanImages.length);
  };

  const showNext = () => {
    setCurrentIndex((index) => (index + 1) % cleanImages.length);
  };

  useEffect(() => {
    if (!open || cleanImages.length < 2) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") showPrevious();
      if (event.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, cleanImages.length]);

  if (cleanImages.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={false}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          width: { xs: "100vw", sm: "92vw" },
          maxWidth: 1180,
          height: { xs: "100dvh", sm: "90vh" },
          maxHeight: { xs: "100dvh", sm: 900 },
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          bgcolor: "#111820",
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(3, 10, 16, .88)" } } }}
      aria-label="גלריית תמונות המכרז"
    >
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", direction: "ltr" }}>
        <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 3 }}>
          <IconButton
            onClick={onClose}
            aria-label="סגירת הגלריה"
            sx={{ color: "white", bgcolor: "rgba(0,0,0,.55)", "&:hover": { bgcolor: "rgba(0,0,0,.75)" } }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center", p: { xs: 1, sm: 2 }, WebkitOverflowScrolling: "touch" }}>
          <Box
            key={`${cleanImages[currentIndex]}-${currentIndex}`}
            component="img"
            src={getBidImageUrl(cleanImages[currentIndex], "lightbox")}
            alt={`תמונת מכרז ${currentIndex + 1} מתוך ${cleanImages.length}`}
            loading="eager"
            onLoad={resetImageRetryState}
            onError={retryImageLoad}
            sx={{ display: "block", width: "auto", height: "auto", maxWidth: "100%", maxHeight: "none", objectFit: "contain", p: "0 !important", m: "0 auto" }}
          />

          {cleanImages.length > 1 && (
            <>
              <IconButton
                onClick={showPrevious}
                aria-label="תמונה קודמת"
                sx={{ position: "absolute", left: { xs: 8, sm: 18 }, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.72)" } }}
              >
                <ArrowBackIosNewRoundedIcon />
              </IconButton>
              <IconButton
                onClick={showNext}
                aria-label="תמונה הבאה"
                sx={{ position: "absolute", right: { xs: 8, sm: 18 }, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.72)" } }}
              >
                <ArrowForwardIosRoundedIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Box sx={{ flex: "0 0 auto", px: { xs: 1, sm: 2 }, pb: { xs: 1.2, sm: 2 }, pt: 0.6, bgcolor: "#111820" }}>
          <Typography sx={{ color: "white", textAlign: "center", mb: 0.8, fontFamily: process.env.REACT_APP_FONT, fontWeight: 700 }}>
            {currentIndex + 1} / {cleanImages.length}
          </Typography>
          {cleanImages.length > 1 && (
            <Stack direction="row" spacing={1} sx={{ overflowX: "auto", justifyContent: { sm: "center" }, pb: 0.4 }}>
              {cleanImages.map((image, index) => (
                <Box
                  key={image}
                  component="button"
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`מעבר לתמונה ${index + 1}`}
                  sx={{ flex: "0 0 auto", width: 70, height: 54, p: 0, borderRadius: 1.5, overflow: "hidden", cursor: "pointer", border: index === currentIndex ? "3px solid #29A7EF" : "2px solid rgba(255,255,255,.3)", bgcolor: "transparent" }}
                >
                  <Box component="img" src={getBidImageUrl(image, "thumbnail")} alt="" loading="lazy" decoding="async" onLoad={resetImageRetryState} onError={retryImageLoad} sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover", p: "0 !important", m: 0 }} />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
